/**
 * cruisePrices.ts — クルーズ船料金表の画面用の純粋関数と API 呼び出し（宮古 2026-10-22・那覇 2026-10-31）
 *
 * クルーズ船の予約（宮古=平良港・那覇=那覇港）だけに使う、車種×日付の1日あたり料金。
 * backend のカレンダー料金より優先する。正: booking-platform/md/pricing.md「クルーズ船料金表」
 * 保存先は sys/api/cruise_prices.php（GET 公開 / POST スタッフの Bearer）。
 *
 * 那覇のクルーズ船プラン（2026-11-02）: 寄港予定で船を選んだ予約だけに使う、プラン × 車種 × 入港日の料金
 * （CDW・NOC・港の送迎料・給油・充電込み）。同じ API の plan_prices / POST の plan。プランの決まりは sys/cruise/plan_lib.php。
 * 正: booking-platform/md/pricing.md「那覇のクルーズ船プラン(寄港の時間帯で4つ)」
 */
import dayjs from 'dayjs';
import { SYS_API_BASE } from '../constants/calendar';
import { withStaffAuth } from './internalApiAuth';
import { getStoreKey } from '../config/backend';

/** 店舗ごとのクルーズ船の値（正: booking-platform/md/pricing.md）。送迎料は料金表に値がない日だけかかる */
export const CRUISE_STORE_INFO = {
  pit: { port: '平良港', surcharge: 4000 },
  ncar: { port: '那覇港', surcharge: 5000 },
} as const;

/** この r-cal の店舗（sys DB の store 列: 宮古='pit'・那覇='ncar'） */
export const cruiseStore = (): 'pit' | 'ncar' => getStoreKey();
const CRUISE_API = `${SYS_API_BASE}/cruise_prices.php`;

/** 那覇のクルーズ船プラン（sys/cruise/plan_lib.php と同じキー・同じ順） */
export const CRUISE_PLANS = [
  { key: 'morning', label: '①朝着', hint: '入港 〜10:30・その日のうちに出港' },
  { key: 'midday', label: '②昼着', hint: '入港 10:31〜13:59・その日のうちに出港' },
  { key: 'afternoon', label: '③午後着', hint: '入港 14:00〜・その日のうちに出港' },
  { key: 'overnight', label: '④1泊', hint: '翌日に出港' },
] as const;
export type CruisePlanKey = typeof CRUISE_PLANS[number]['key'];
/** プランを出す車種（那覇）。アルファード40系・20系・ハイエースGC はクルーズ船の予約に出さない */
export const CRUISE_PLAN_VEHICLES = ['CP-VN-F', 'CP-A', 'M-EC-G', 'H-KN-M', 'B-SLP6-H', 'M-D5-H', 'T-AV-H'];
/** プランがある店舗 */
export const hasCruisePlans = (store: 'pit' | 'ncar' = cruiseStore()): boolean => store === 'ncar';

/** 'YYYY-MM-DD' → 円 */
export type DayPrices = Record<string, number>;
/** 入力欄の下書き。'' = クルーズ料金なし */
export type DraftPrices = Record<string, string>;

export interface CruiseSaveItem {
  vehicle_type_code: string;
  date: string;
  price: number | null;
  /** 付けるとクルーズ船プランの料金（那覇だけ）。無ければ日別のクルーズ料金 */
  plan?: CruisePlanKey;
}

/** プラン → 車種コード → 日付 → 円 */
export type PlanPrices = Partial<Record<CruisePlanKey, Record<string, DayPrices>>>;

/** 月のカレンダー（日曜始まり）。月外のマスは null */
export function buildMonthWeeks(month: string): (string | null)[][] {
  const first = dayjs(`${month}-01`);
  const days = first.daysInMonth();
  const cells: (string | null)[] = Array(first.day()).fill(null);
  for (let d = 1; d <= days; d++) cells.push(first.date(d).format('YYYY-MM-DD'));
  while (cells.length % 7 !== 0) cells.push(null);
  const weeks: (string | null)[][] = [];
  for (let i = 0; i < cells.length; i += 7) weeks.push(cells.slice(i, i + 7));
  return weeks;
}

/** 保存済みの値から入力欄の下書きを作る */
export function toDraft(saved: DayPrices): DraftPrices {
  const out: DraftPrices = {};
  Object.entries(saved).forEach(([d, p]) => { out[d] = String(p); });
  return out;
}

/** 入力値を検証して円にする。'' は null（なし）。不正は undefined */
export function parsePriceInput(raw: string): number | null | undefined {
  const s = raw.replace(/[,，\s¥￥円]/g, '');
  if (s === '') return null;
  if (!/^\d+$/.test(s)) return undefined;
  const n = Number(s);
  return n >= 1 && n <= 1000000 ? n : undefined;
}

/**
 * 保存済みと下書きの差分を保存リクエストにする。
 * 不正な入力があれば invalid にその日付を入れ、items には含めない（画面で保存を止める）。
 */
export function diffDraft(code: string, saved: DayPrices, draft: DraftPrices, plan?: CruisePlanKey): { items: CruiseSaveItem[]; invalid: string[] } {
  const items: CruiseSaveItem[] = [];
  const invalid: string[] = [];
  const dates = new Set([...Object.keys(saved), ...Object.keys(draft)]);
  Array.from(dates).sort().forEach(date => {
    const parsed = parsePriceInput(draft[date] ?? '');
    if (parsed === undefined) { invalid.push(date); return; }
    const before = saved[date] ?? null;
    if (parsed !== before) items.push(plan ? { vehicle_type_code: code, date, price: parsed, plan } : { vehicle_type_code: code, date, price: parsed });
  });
  return { items, invalid };
}

/** from〜to（両端含む）の各日に同じ値を入れた下書きを返す。'' なら消す */
export function fillRange(draft: DraftPrices, from: string, to: string, value: string): DraftPrices {
  const out = { ...draft };
  let d = dayjs(from);
  const end = dayjs(to);
  if (!d.isValid() || !end.isValid() || d.isAfter(end)) return out;
  while (!d.isAfter(end)) {
    out[d.format('YYYY-MM-DD')] = value;
    d = d.add(1, 'day');
  }
  return out;
}

/** 期間内のクルーズ料金（日別: 車種コード → 日付 → 円）とクルーズ船プランの料金（那覇） */
export async function fetchCruisePriceTables(from: string, to: string): Promise<{ prices: Record<string, DayPrices>; planPrices: PlanPrices }> {
  const res = await fetch(`${CRUISE_API}?store=${cruiseStore()}&from=${from}&to=${to}`);
  const body = await res.json().catch(() => null);
  if (!res.ok || !body?.ok) throw new Error(body?.error || `HTTP ${res.status}`);
  return { prices: body.prices || {}, planPrices: body.plan_prices || {} };
}

/** 期間内のクルーズ料金（車種コード → 日付 → 円） */
export async function fetchCruisePrices(from: string, to: string): Promise<Record<string, DayPrices>> {
  return (await fetchCruisePriceTables(from, to)).prices;
}

/** 保存（スタッフの Bearer）。サーバが読み直して確かめた件数が合わなければ失敗にする */
export async function saveCruisePrices(items: CruiseSaveItem[]): Promise<{ saved: number; deleted: number }> {
  let token: string | null = null;
  try { token = window.localStorage.getItem('authToken'); } catch { token = null; }
  const res = await fetch(CRUISE_API, {
    method: 'POST',
    headers: withStaffAuth({ 'Content-Type': 'application/json' }, token),
    body: JSON.stringify({ store: cruiseStore(), items }),
  });
  const body = await res.json().catch(() => null);
  if (!res.ok || !body?.ok) throw new Error(body?.error || `HTTP ${res.status}`);
  if (body.verified !== items.length) throw new Error(`保存を確認できませんでした（${body.verified}/${items.length} 件）`);
  return { saved: body.saved, deleted: body.deleted };
}
